import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  HomeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  DocumentDuplicateIcon,
  BookOpenIcon,
  WrenchScrewdriverIcon,
  MegaphoneIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { H3, Badge } from '../ui';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  // `requiresAdmin` covers any org-scoped admin (principal, org_admin,
  // super_admin) -- mirrors `is_superuser` on the server's User model.
  // `requiresSuperAdmin` is the stricter form: only true platform
  // super-admins (cross-tenant) -- used for views like /admin/orgs that
  // span all schools. The legacy `requiresStaff` predicate has been
  // removed; `is_staff` is true for plain teachers (Django leftover) and
  // is unsafe as an authorisation gate.
  requiresAdmin?: boolean;
  requiresSuperAdmin?: boolean;
  badge?: string;
  devOnly?: boolean;
}

const navigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    name: 'Classrooms',
    href: '/classrooms',
    icon: AcademicCapIcon,
  },
  {
    name: 'Curriculum',
    href: '/curriculum',
    icon: BookOpenIcon,
  },
  {
    name: 'Children',
    href: '/children',
    icon: UserGroupIcon,
  },
  {
    name: 'Observations',
    href: '/observations',
    icon: ClipboardDocumentListIcon,
  },
  {
    name: 'Bulk Observations',
    href: '/bulk-observations',
    icon: DocumentDuplicateIcon,
  },
  {
    name: 'Assessments',
    href: '/assessments',
    icon: DocumentTextIcon,
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: ChartBarIcon,
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: ChartBarIcon,
    requiresAdmin: true,
  },
  {
    name: 'Users',
    href: '/users',
    icon: UserGroupIcon,
    requiresAdmin: true,
  },
  {
    name: 'Organizations',
    href: '/admin/orgs',
    icon: BuildingOffice2Icon,
    requiresSuperAdmin: true,
  },
  {
    name: 'Leads',
    href: '/admin/leads',
    icon: MegaphoneIcon,
    requiresSuperAdmin: true,
  },
  {
    name: 'Dev Tools',
    href: '/dev',
    icon: WrenchScrewdriverIcon,
    devOnly: true,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Cog6ToothIcon,
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Filter navigation based on user permissions and feature flags
  const isPlatformSuperAdmin = !!user?.roles?.includes('super_admin');
  const filteredNavigation = navigation.filter(item => {
    if (item.requiresSuperAdmin && !isPlatformSuperAdmin) {
      return false;
    }
    if (item.requiresAdmin && !user?.is_superuser) {
      return false;
    }
    // Hide dev-only items in production
    if (item.devOnly && !import.meta.env.DEV) {
      return false;
    }
    return true;
  });

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40
      }
    }
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary-500 rounded-xl mr-3">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <H3 className="text-gray-900 dark:text-gray-100">Sprout</H3>
        </div>

        {/* Close button - mobile only */}
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {filteredNavigation.map((item) => {
          const isActive = location.pathname === item.href ||
                         (item.href !== '/dashboard' && location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={({ isActive }) => `
                flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                }
              `}
            >
              <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-primary-500' : 'text-gray-500 dark:text-gray-400'}`} />
              {item.name}
              {item.badge && (
                <Badge variant="info" size="sm" className="ml-auto">
                  {item.badge}
                </Badge>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User info footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-medium text-sm">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.roles?.[0]
                ? user.roles[0].charAt(0).toUpperCase() + user.roles[0].slice(1)
                : user?.is_staff ? 'Staff' : 'User'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar with animation */}
      <motion.div
        variants={sidebarVariants}
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700 lg:hidden"
      >
        {sidebarContent}
      </motion.div>

      {/* Desktop sidebar - always visible, no animation */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 z-40 bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700 transition-colors duration-200">
        {sidebarContent}
      </div>
    </>
  );
};

export default Sidebar;
